import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';

import Layout from '@/components/template/Layout';
import HeadTitle from '@/components/ui/custom/HeadTitle';
import PageTitle from '@/components/ui/custom/PageTitle';
import Text from '@/components/ui/custom/dataDisplay/Text';
import LoadingView from '@/pages/courses/LoadingView';
import useStudentContext from '@/data/hook/useStudentContext';
import ExtraCourseCard from './components/ExtraCourseCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { CourseProgressTabEnum } from '@/enums/CourseProgressTabEnum';
import StudentDashboardStats from '@/models/StudentDashboard';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { separateCoursesByProgress } from '@/utils/courseProgress';

export default function ExtraCoursesPortal() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'extraCoursesPortal',
  });

  const {
    extraCoursesStats,
    loadingDashboard: loading,
    dashboardStatsError: error,
    dashboardStats,
    selectProgress,
  } = useStudentContext();

  const { inProgressCoursesStats, completedCoursesStats } = useMemo(() => {
    const { completed, inProgress } =
      separateCoursesByProgress(extraCoursesStats);

    return {
      inProgressCoursesStats: inProgress,
      completedCoursesStats: completed,
    };
  }, [extraCoursesStats]);

  const [selectedTab, setSelectedTab] = useState<CourseProgressTabEnum | null>(
    null,
  );

  if (!dashboardStats) return null;

  return (
    <Layout>
      <HeadTitle routeInfo={t('extraCourses')} />
      <PageTitle headingText={t('extraCourses')} position="mb-0" />
      <ConditionalRenderer condition={loading}>
        <LoadingView />
      </ConditionalRenderer>

      <ConditionalRenderer condition={error}>
        <Text
          text={t('errorLoadingCourses')}
          format="rubik-500"
          className="text-primary"
        />
      </ConditionalRenderer>

      <ConditionalRenderer condition={extraCoursesStats.length > 0}>
        <main className="flex flex-col gap-6">
          <ConditionalRenderer
            condition={
              inProgressCoursesStats.length > 0 &&
              selectedTab !== CourseProgressTabEnum.CONCLUDED
            }
          >
            <ExtraCoursesContainer
              tab={CourseProgressTabEnum.IN_PROGRESS}
              dashboardStatsCollection={inProgressCoursesStats}
              selected={selectedTab === CourseProgressTabEnum.IN_PROGRESS}
              onSelectTab={setSelectedTab}
              onSelectCourse={selectProgress}
            />
          </ConditionalRenderer>

          <ConditionalRenderer
            condition={
              completedCoursesStats.length > 0 &&
              selectedTab !== CourseProgressTabEnum.IN_PROGRESS
            }
          >
            <ExtraCoursesContainer
              tab={CourseProgressTabEnum.CONCLUDED}
              dashboardStatsCollection={completedCoursesStats}
              selected={selectedTab === CourseProgressTabEnum.CONCLUDED}
              onSelectTab={setSelectedTab}
              onSelectCourse={selectProgress}
            />
          </ConditionalRenderer>
        </main>
      </ConditionalRenderer>
    </Layout>
  );
}

type ExtraCoursesContainerProps = {
  tab: CourseProgressTabEnum | null;
  dashboardStatsCollection: StudentDashboardStats[];
  selected?: boolean;
  onSelectTab: (tab: CourseProgressTabEnum | null) => void;
  onSelectCourse: (courseProgressId: number) => void;
};

function ExtraCoursesContainer(props: ExtraCoursesContainerProps) {
  const {
    tab,
    dashboardStatsCollection,
    selected,
    onSelectTab,
    onSelectCourse,
  } = props;

  const [t] = useTranslation('translation', {
    keyPrefix: 'coursesView.admin.groupBy',
  });

  const [tCourseProgress] = useTranslation('translation', {
    keyPrefix: 'extraCoursesPortal',
  });

  const buttonText = selected ? t('back') : t('all');

  const styles: Record<
    CourseProgressTabEnum,
    { title: string; color: string }
  > = {
    [CourseProgressTabEnum.IN_PROGRESS]: {
      title: tCourseProgress('inProgress'),
      color: 'text-warning',
    },
    [CourseProgressTabEnum.CONCLUDED]: {
      title: tCourseProgress('completed'),
      color: 'text-success',
    },
  };

  const style = styles[tab ?? CourseProgressTabEnum.IN_PROGRESS];

  function handleSelect() {
    if (selected) {
      onSelectTab(null);
    } else {
      onSelectTab(tab);
    }
  }

  return (
    <section
      className={twMerge(
        'flex flex-col gap-2.5 border border-neutral-content rounded-lg p-2.5',
        selected && 'border-none',
      )}
    >
      <header className="flex justify-between items-center">
        <Text
          text={style.title}
          format="rubik-500"
          className={twJoin(style.color, 'text-[1.125rem]')}
        />
        <ConditionalRenderer condition={dashboardStatsCollection.length > 1}>
          <MainButton
            text={buttonText}
            color="custom"
            className={twMerge(
              'font-600 text-14 text-primary gap-1.5',
              !selected && 'flex-row-reverse',
            )}
            icon={
              <ChevronRightIcon
                className={twJoin(
                  'w-4 h-4 ease-in-out transition-all duration-200',
                  selected && 'rotate-180',
                )}
              />
            }
            onClick={handleSelect}
          />
        </ConditionalRenderer>
      </header>
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {dashboardStatsCollection.map(dashboardStats => (
          <ExtraCourseCard
            key={dashboardStats.id}
            dashboardStats={dashboardStats}
            onClick={onSelectCourse}
          />
        ))}
      </ul>
    </section>
  );
}
